// src/pages/Users/News/NewsPage.jsx

import React, { useState } from "react";
import { FaNewspaper } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Reveal from "./../../../components/Reveal/Reveal";

import { createNews } from "../../../services/newsServices";

import useNewsController from "./hooks/useNewsController";

import NewsFeaturedCarousel from "./components/NewsFeaturedCarousel";
import NewsToolbar from "./components/NewsToolbar";
import NewsCard from "./components/NewsCard";
import NewsSkeleton from "./components/NewsSkeleton";
import EmptyState from "./components/EmptyState";

import CreateNewsModal from "./CreateNewsModal";
import EditNewsModal from "./EditNewsModal";

export default function NewsPage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);

  const {
    loading,
    error,
    newsList,
    filteredSorted,
    visible,
    featuredNews,
    ui,
    searchInput,
    setSearchInput,
    updateUi,
    resetFilters,
    loadMore,
    refreshNewsList,
  } = useNewsController({ isManager });

  const handleAddNewsClick = async () => {
    if (!user || !user.userId) {
      toast.error("Utilisateur non identifié.");
      return;
    }

    try {
      const draft = await createNews({
        title: "",
        content: "",
        image_url: "",
        author_id: user.userId,
        is_active: true,
      });

      setCurrentNews(draft);
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error("Erreur création actualité", error);
      toast.error("Erreur lors de la création de l'actualité.");
    }
  };

  const handleOpenEdit = (news) => {
    setCurrentNews(news);
    setIsEditModalOpen(true);
  };

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Actualités & Résultats"
          subtitle="Reste à jour avec les dernières actualités du club, les résultats des compétitions et les événements à venir."
          icon={FaNewspaper}
          align="left"
          className="mb-4"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        {featuredNews.length > 0 && (
          <Reveal>
            <NewsFeaturedCarousel items={featuredNews} />
          </Reveal>
        )}

        <div className="sticky top-[76px] z-30 -mx-3 mt-4 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
          <NewsToolbar
            ui={ui}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            isManager={isManager}
            filteredCount={filteredSorted.length}
            totalCount={newsList.length}
            onChange={updateUi}
            onReset={resetFilters}
            onCreate={handleAddNewsClick}
          />
        </div>

        <section className="mt-5">
          {loading && <NewsSkeleton />}

          {!loading && error && (
            <EmptyState
              title="Actualités indisponibles"
              description={error}
              onReset={refreshNewsList}
              resetLabel="Réessayer"
            />
          )}

          {!loading && !error && filteredSorted.length === 0 && (
            <EmptyState
              title="Aucune actualité trouvée"
              description="Aucune actualité ne correspond à ta recherche ou aux filtres sélectionnés."
              onReset={resetFilters}
            />
          )}

          {!loading && !error && filteredSorted.length > 0 && (
            <>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                {visible.map((news) => (
                  <NewsCard
                    key={news.id}
                    news={news}
                    canModify={isManager}
                    onDeleted={refreshNewsList}
                    onEdited={refreshNewsList}
                    onEdit={handleOpenEdit}
                  />
                ))}
              </div>

              {visible.length < filteredSorted.length && (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-slate-950 px-6 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
                  >
                    Charger plus d’actualités
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      <CreateNewsModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateSuccess={async () => {
          setIsCreateModalOpen(false);
          await refreshNewsList();
          toast.success("Actualité créée avec succès");
        }}
        news={currentNews}
      />

      <EditNewsModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setCurrentNews(null);
        }}
        refreshList={refreshNewsList}
        news={currentNews}
      />

      <ToastContainer />
    </section>
  );
}
